import {computed, type Ref, type ComputedRef} from 'vue';
import {assignIf, dateAfter, dateBefore, maxLength, minLength, required} from '@regle/rules';
import {storeToRefs} from 'pinia';
import {useScopedRegle} from '@malt/forms';
import {useRulesStore} from '~/store/rules/rulesStore';
import type {EditExperience} from '../types/EditExperience';

// TODO move Experience from domain and create a type not a class!

type UseExperienceFormOptions = {
    experience?: Ref<EditExperience>;
    hiddenFields?: ComputedRef<Array<keyof EditExperience>>;
    isLockedToMaltProject?: boolean;
};

export function useExperienceForm({experience, hiddenFields, isLockedToMaltProject}: UseExperienceFormOptions) {
    const rulesStore = useRulesStore();
    const {
        experienceDescriptionLength: ruleExperienceDescriptionLength,
        experienceTitleLength: ruleExperienceTitleLength,
        experienceClientCompanyLength: ruleExperienceClientCompanyLength,
        experienceMaxSkills: ruleExperienceMaxSkills,
        experienceFixedDateMin,
        experienceFixedDateMax,
    } = storeToRefs(rulesStore);

    // Start date maximum value should be the end date of the experience
    const experienceStartDateMin = computed(() => experienceFixedDateMin.value);
    const experienceStartDateMax = computed(() => experienceFixedDateMax.value);

    // End date minimum value should be the start date of the experience
    const experienceEndDateMin = computed(() => {
        if (experience?.value.startDate && !experience?.value.currentJob) {
            return experience.value.startDate;
        }
        return experienceFixedDateMin.value;
    });
    const experienceEndDateMax = computed(() => experienceFixedDateMax.value);

    const isValidExperienceRequest = computed(() => {
        return !r$.title.$invalid && !r$.company.$invalid && !r$.startDate.$invalid && !r$.endDate.$invalid && !r$.skills.$invalid;
    });

    const {r$} = useScopedRegle(
        experience,
        () => {
            const isCompanyValidationEnabled = !isLockedToMaltProject && !hiddenFields?.value.includes('company');

            return {
                title: {
                    required,
                    minLength: minLength(ruleExperienceTitleLength.value.min),
                    maxLength: maxLength(ruleExperienceTitleLength.value.max),
                },
                description: {
                    required,
                    minLength: minLength(ruleExperienceDescriptionLength.value.min),
                    maxLength: maxLength(ruleExperienceDescriptionLength.value.max),
                },
                company: assignIf(isCompanyValidationEnabled, {
                    required,
                    minLength: minLength(ruleExperienceClientCompanyLength.value.min),
                    maxLength: maxLength(ruleExperienceClientCompanyLength.value.max),
                }),
                startDate: {
                    required,
                    dateAfter: dateAfter(experienceStartDateMin, {allowEqual: true}),
                    dateBefore: dateBefore(experienceStartDateMax, {allowEqual: true}),
                },
                endDate: assignIf(() => !experience?.value.currentJob, {
                    required,
                    dateAfter: dateAfter(experienceEndDateMin, {allowEqual: true}),
                    dateBefore: dateBefore(experienceEndDateMax, {allowEqual: true}),
                }),
                skills: {
                    maxLength: maxLength(ruleExperienceMaxSkills.value),
                    $each: {},
                },
            };
        },
        {
            namespace: 'experience-form',
            /* Touch only the fields that are non-empty on mount. https://reglejs.dev/core-concepts/modifiers#immediatedirty */
            immediateDirty: 'non-empty',
        },
    );

    return {
        r$,
        experienceStartDateMin,
        experienceStartDateMax,
        experienceEndDateMin,
        experienceEndDateMax,
        isValidExperienceRequest,
    };
}
